import React, { useState } from "react";
import crypto from "crypto"; // To generate a secret key
import { ethers } from "ethers";
import blockchainConfig from "../blockchain/config";
import contractAbi from "../blockchain/ProvablyFairRNG.abi.json";

const ConfigPanel = ({ onSubmit, onGenerateSecret, signer, setSessionId }) => {
  const [numWinners, setNumWinners] = useState(1);
  const [reward, setReward] = useState(100);
  const [lockType, setLockType] = useState("locked");
  const [lockDuration, setLockDuration] = useState(0);
  const [vestDuration, setVestDuration] = useState(0);
  const [generatedSecret, setGeneratedSecret] = useState("");
  const [statusMessage, setStatusMessage] = useState("");

  const generateSecretAndStartSession = async () => {
    if (!signer) {
      setStatusMessage("Signer is not available. Connect your wallet first!");
      return;
    }

    try {
      const contract = new ethers.Contract(
        blockchainConfig.contractAddress,
        contractAbi,
        signer
      );

      setStatusMessage(
        "Starting a new session (any active session will be dropped)..."
      );
      // Call startNewSession to create a new session on-chain.
      const tx = await contract.startNewSession();
      const receipt = await tx.wait();

      // Parse logs to find the SessionStarted event.
      const sessionStartedEvent = receipt.logs
        .map((log) => {
          try {
            return contract.interface.parseLog(log);
          } catch (error) {
            return null;
          }
        })
        .find((event) => event && event.name === "SessionStarted");

      if (!sessionStartedEvent) {
        throw new Error(
          "SessionStarted event not found in the transaction receipt."
        );
      }

      const sessionId = sessionStartedEvent.args.sessionId;
      setSessionId(sessionId); // update parent with the new sessionId

      setStatusMessage(
        `New session started! Session ID: ${sessionId.toString()}`
      );

      // Generate a secret using crypto.randomBytes and add a "0x" prefix.
      const secretBuffer = crypto.randomBytes(32);
      const secret = "0x" + secretBuffer.toString("hex");
      setGeneratedSecret(secret);
      if (onGenerateSecret) {
        onGenerateSecret(secret);
      }
      console.log("Generated Secret:", secret);
      console.log("Secret length (characters):", secret.length); // Should be 66
    } catch (error) {
      console.error("Error starting session or generating secret:", error);
      setStatusMessage(
        "An unexpected error occurred. Check the console for details."
      );
    }
  };

  const handleSubmit = () => {
    if (!generatedSecret) {
      setStatusMessage(
        "Please generate a secret before submitting the configuration."
      );
      return;
    }
    if (numWinners < 1 || reward <= 0) {
      setStatusMessage(
        "Number of winners must be at least 1 and reward must be greater than 0."
      );
      return;
    }
    onSubmit({
      numWinners,
      reward,
      lockType,
      lockDuration,
      vestDuration,
    });
  };

  return (
    <div className="p-8 bg-gray-900 border border-purple-400 shadow-xl bg-opacity-90 backdrop-filter backdrop-blur-lg rounded-2xl border-opacity-30">
      <h2 className="mb-6 text-2xl font-bold text-center">Configuration</h2>
      {/* Form Fields */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="flex flex-col">
          <label className="mb-2">Number of Winners</label>
          <input
            type="number"
            value={numWinners}
            onChange={(e) => setNumWinners(parseInt(e.target.value))}
            min={1}
            className="p-3 bg-transparent border border-gray-700 rounded-lg"
            placeholder="e.g., 3"
          />
        </div>
        <div className="flex flex-col">
          <label className="mb-2">Reward per Winner (tokens)</label>
          <input
            type="number"
            value={reward}
            onChange={(e) => setReward(parseFloat(e.target.value))}
            min={0}
            className="p-3 bg-transparent border border-gray-700 rounded-lg"
            placeholder="e.g., 100"
          />
        </div>
        <div className="flex flex-col md:col-span-2">
          <label className="mb-2">Locking Period</label>
          <select
            value={lockType}
            onChange={(e) => setLockType(e.target.value)}
            className="p-3 bg-gray-900 border border-gray-700 rounded-lg"
          >
            <option value="locked">Locked</option>
            <option value="vested">Vested</option>
            <option value="locked_and_vested">Locked and Vested</option>
          </select>
        </div>
        {lockType === "locked" && (
          <div className="flex flex-col">
            <label className="mb-2">Lock Duration (days)</label>
            <input
              type="number"
              value={lockDuration}
              onChange={(e) => setLockDuration(parseInt(e.target.value))}
              min={0}
              className="p-3 bg-transparent border border-gray-700 rounded-lg"
              placeholder="e.g., 30"
            />
          </div>
        )}
        {lockType === "vested" && (
          <div className="flex flex-col">
            <label className="mb-2">Vest Duration (days)</label>
            <input
              type="number"
              value={vestDuration}
              onChange={(e) => setVestDuration(parseInt(e.target.value))}
              min={0}
              className="p-3 bg-transparent border border-gray-700 rounded-lg"
              placeholder="e.g., 60"
            />
          </div>
        )}
        {lockType === "locked_and_vested" && (
          <>
            <div className="flex flex-col">
              <label className="mb-2">Lock Duration (days)</label>
              <input
                type="number"
                value={lockDuration}
                onChange={(e) => setLockDuration(parseInt(e.target.value))}
                min={0}
                className="p-3 bg-transparent border border-gray-700 rounded-lg"
                placeholder="e.g., 30"
              />
            </div>
            <div className="flex flex-col">
              <label className="mb-2">Vest Duration (days)</label>
              <input
                type="number"
                value={vestDuration}
                onChange={(e) => setVestDuration(parseInt(e.target.value))}
                min={0}
                className="p-3 bg-transparent border border-gray-700 rounded-lg"
                placeholder="e.g., 60"
              />
            </div>
          </>
        )}
      </div>
      {/* Buttons */}
      <div className="flex flex-col items-center mt-6 space-y-4">
        <button
          onClick={generateSecretAndStartSession}
          className="w-full px-6 py-3 font-semibold text-white bg-blue-500 rounded-full hover:bg-blue-600"
        >
          Generate Secret Key &amp; Start New Session
        </button>

        {generatedSecret && (
          <div className="flex flex-col items-center w-full">
            <div className="w-full overflow-x-auto">
              <span className="block px-4 py-2 text-sm text-white break-all bg-gray-800 rounded-md">
                <strong>Generated Secret:</strong> {generatedSecret}
              </span>
            </div>
          </div>
        )}

        <button
          onClick={handleSubmit}
          className="w-full px-6 py-3 font-semibold text-white bg-indigo-500 rounded-full hover:bg-indigo-600"
        >
          Submit Configuration
        </button>

        {statusMessage && <p className="mt-4 text-red-500">{statusMessage}</p>}
      </div>
    </div>
  );
};

export default ConfigPanel;
